import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Clock, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const SERVICES_OFFERED = [
  "Permit Expediting & Strategy", "Due Diligence & Preliminary Research", "Entitlements & Planning Approval",
  "Zoning Variance / CUP", "Building, MEP & Trades", "Accessibility / ADA Compliance",
  "Coastal Development Permit", "Health Department Approval", "Sign Permits", "New Construction",
  "Land Use Permits", "Liquor License", "Change of Use", "Green Building Approval", "Cal/OSHA",
  "Fire Approval", "National Brand Program", "AHJ Intel Subscription", "Enterprise Platform Access",
  "Other / Not Sure Yet",
];

export default function Hire() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", company: "",
    project_name: "", jurisdiction: "", service_needed: "Permit Expediting & Strategy",
    scope_description: "", address: "", valuation: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        contact_name: f.contact_name || user.name || "",
        contact_email: f.contact_email || user.email || "",
      }));
    }
  }, [user]);

  const validate = () => {
    const e = {};
    if (!form.contact_email.trim()) e.contact_email = "Email is required so we can quote you back.";
    else if (!/^\S+@\S+\.\S+$/.test(form.contact_email.trim())) e.contact_email = "That email doesn't look right.";
    if (!form.project_name.trim()) e.project_name = "Give your project a name (anything works).";
    if (!form.scope_description.trim()) e.scope_description = "Tell us a bit about the scope so we can quote accurately.";
    else if (form.scope_description.trim().length < 12) e.scope_description = "A little more detail helps — 1–2 sentences minimum.";
    return e;
  };

  const submit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please fix the highlighted fields.");
      // Scroll to first error
      const firstKey = Object.keys(e)[0];
      const el = document.querySelector(`[data-testid="hire-${firstKey === "contact_email" ? "email" : firstKey === "project_name" ? "project-name" : "scope"}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/engagements", { ...form, valuation: Number(form.valuation) || 0 });
      setSubmitted(true);
      toast.success("Lead submitted — we'll respond within 24 hours.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Please email Admin@Ctcpermits.com directly.");
    } finally { setSubmitting(false); }
  };

  const clearError = (key) => {
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="hire-page">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-3">Contact</div>
            <h1 className="font-outfit text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              Let&apos;s Talk <br /><span className="text-slate-400">About Your Project</span>
            </h1>
            <p className="mt-6 text-base text-slate-300 leading-relaxed">
              Whether you&apos;re navigating a complex review cycle, planning a national rollout, or need
              a pre-acquisition code assessment — we&apos;re ready.
            </p>

            <div className="mt-10 space-y-5">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-400 mb-1.5">Email</div>
                <a href="mailto:Admin@Ctcpermits.com" className="text-lg font-mono text-white hover:text-emerald-400 transition">
                  Admin@Ctcpermits.com
                </a>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-400 mb-1.5">Response time</div>
                <p className="text-sm text-slate-300 leading-relaxed max-w-sm">
                  To speak with our team, reach out via email and a permitting specialist will respond within
                  24 business hours.
                </p>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-400 mb-1.5">Location</div>
                <div className="text-sm text-slate-300">Dallas, TX — Serving All 50 States</div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md">
              {[
                { i: CheckCircle2, t: "Step 1", s: "Tell us the scope" },
                { i: Clock, t: "Step 2", s: "24-hr quote" },
                { i: ArrowRight, t: "Step 3", s: "Live workspace" },
              ].map((c) => {
                const Ico = c.i;
                return (
                  <div key={c.t} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                    <Ico className="w-3.5 h-3.5 text-emerald-400 mb-2" />
                    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-400">{c.t}</div>
                    <div className="text-xs text-slate-200 mt-1 leading-tight">{c.s}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-8 md:p-10" data-testid="hire-submitted">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-4" />
                <h2 className="font-outfit text-3xl font-bold mb-3">Got it, thanks.</h2>
                <p className="text-slate-200 max-w-xl">
                  Your lead is in our queue. A permitting specialist will reply within 24 business hours
                  with a tailored quote and next steps. You can also email{" "}
                  <a href="mailto:Admin@Ctcpermits.com" className="text-emerald-400 hover:underline font-mono">Admin@Ctcpermits.com</a>{" "}
                  directly if it&apos;s time-sensitive.
                </p>
                {user && (
                  <div className="mt-6">
                    <Button asChild className="bg-emerald-600 hover:bg-emerald-500 text-white">
                      <Link to="/map">Explore the AHJ map<ArrowRight className="ml-2 w-4 h-4" /></Link>
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fn" className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">First name</Label>
                    <Input id="fn" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      placeholder="John Smith" className="mt-1.5 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="hire-name" />
                  </div>
                  <div>
                    <Label htmlFor="co" className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Company</Label>
                    <Input id="co" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                      placeholder="Your company or firm" className="mt-1.5 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="hire-company" />
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="em" className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Email *</Label>
                  <Input id="em" type="email" value={form.contact_email}
                    onChange={(e) => { setForm({ ...form, contact_email: e.target.value }); clearError("contact_email"); }}
                    placeholder="john@company.com"
                    className={`mt-1.5 bg-slate-900/50 text-white placeholder:text-slate-500 ${errors.contact_email ? "border-red-500 focus-visible:ring-red-500/30" : "border-slate-700"}`}
                    data-testid="hire-email" />
                  {errors.contact_email && <p className="mt-1.5 text-xs text-red-400 font-mono" data-testid="hire-email-error">{errors.contact_email}</p>}
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pn" className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Project name *</Label>
                    <Input id="pn" value={form.project_name}
                      onChange={(e) => { setForm({ ...form, project_name: e.target.value }); clearError("project_name"); }}
                      placeholder="e.g. Mecklenburg TI at 4500 Park Rd"
                      className={`mt-1.5 bg-slate-900/50 text-white placeholder:text-slate-500 ${errors.project_name ? "border-red-500 focus-visible:ring-red-500/30" : "border-slate-700"}`}
                      data-testid="hire-project-name" />
                    {errors.project_name && <p className="mt-1.5 text-xs text-red-400 font-mono" data-testid="hire-project-name-error">{errors.project_name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="ju" className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Jurisdiction (AHJ)</Label>
                    <Input id="ju" value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                      placeholder="Charlotte, NC · Mecklenburg County" className="mt-1.5 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="hire-jurisdiction" />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Service needed</Label>
                  <Select value={form.service_needed} onValueChange={(v) => setForm({ ...form, service_needed: v })}>
                    <SelectTrigger data-testid="hire-service" className="mt-1.5 bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px]">
                      {SERVICES_OFFERED.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Site address</Label>
                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Optional" className="mt-1.5 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Project valuation ($)</Label>
                    <Input type="number" min="0" value={form.valuation} onChange={(e) => setForm({ ...form, valuation: e.target.value })}
                      placeholder="0" className="mt-1.5 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="sd" className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Project scope *</Label>
                  <Textarea id="sd" rows={5} value={form.scope_description}
                    onChange={(e) => { setForm({ ...form, scope_description: e.target.value }); clearError("scope_description"); }}
                    placeholder="Tell us about your project — scope, timeline, constraints, deferred submittals, etc."
                    className={`mt-1.5 bg-slate-900/50 text-white placeholder:text-slate-500 ${errors.scope_description ? "border-red-500 focus-visible:ring-red-500/30" : "border-slate-700"}`}
                    data-testid="hire-scope" />
                  {errors.scope_description && <p className="mt-1.5 text-xs text-red-400 font-mono" data-testid="hire-scope-error">{errors.scope_description}</p>}
                </div>
                <Button onClick={submit} disabled={submitting} data-testid="hire-submit"
                  className="mt-6 w-full bg-emerald-600 hover:bg-emerald-500 text-white h-12 text-base">
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</>) :
                    (<>Submit project — get a 24-hour quote<ArrowRight className="ml-2 w-4 h-4" /></>)}
                </Button>
                <p className="mt-3 text-xs text-slate-500 text-center font-mono">
                  Prefer email? <a href="mailto:Admin@Ctcpermits.com" className="text-emerald-400 hover:underline">Admin@Ctcpermits.com</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
